'use client';

import { decodePassphrase } from '@/lib/client-utils';
import { DebugMode } from '@/lib/Debug';
import { RecordingIndicator } from '@/lib/RecordingIndicator';
import { SettingsMenu } from '@/lib/SettingsMenu';
import { ConnectionDetails } from '@/lib/types';
import {
  formatChatMessageLinks,
  LiveKitRoom,
  LocalUserChoices,
  PreJoin,
  VideoConference
} from '@livekit/components-react';
import {
  ExternalE2EEKeyProvider,
  RoomOptions,
  VideoCodec,
  VideoPresets,
  Room,
  DeviceUnsupportedError,
  RoomConnectOptions,
} from 'livekit-client';
import { useRouter } from 'next/navigation';
import React from 'react';
import moment from 'moment';
import { profile } from 'console';
import {CustomVideoConference} from './CustomVideoConference'
import { READ_ONLY_ROLE, type RoomMediaPolicy } from './mediaPolicy'
import {
  LOBBY_POLL_INTERVAL_MS,
  type LobbyTicketResponse,
  type LobbyTicketStatus,
} from './lobby';
import { resolveSessionParams, type SessionParams } from './sessionParams';
import { WebSocketStreamer } from './WebSocketStreamer';

const CONN_DETAILS_ENDPOINT =
  process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details';
const SHOW_SETTINGS_MENU = process.env.NEXT_PUBLIC_SHOW_SETTINGS_MENU == 'true';

export function PageClientImpl(props: {
  roomName: string;
  region?: string;
  hq: boolean;
  codec: VideoCodec;
  userId?: string;
  partitionId?: string;
  randomParticipantPostfix?: string;
  viewer?: string;
}) {
  const [preJoinChoices, setPreJoinChoices] = React.useState<LocalUserChoices | undefined>(
    undefined,
  );

  const [roomTitle, setRoomTitle] = React.useState("");
  // `undefined` = still resolving from BDS, '' = resolved with no name. The two
  // must stay distinct: the loading gate below keys off `undefined`, so using ''
  // for both would leave a nameless user staring at the spinner forever.
  const [roomUser, setRoomUser] = React.useState<string | undefined>(undefined);
  const [userRole, setUserRole] = React.useState("");
  const [preJoinParticipants, setPreJoinParticipants] = React.useState<string[]>([]);
  // Room-wide policy read BEFORE connecting, so we never publish a track the
  // room has muted and then yank it back a beat later.
  const [preJoinPolicy, setPreJoinPolicy] = React.useState<RoomMediaPolicy>({});
  const [isLoadingParticipants, setIsLoadingParticipants] = React.useState(false);

  const preJoinDefaults = React.useMemo(() => {
    return {
      username: roomUser ?? '',
      videoEnabled: true,
      audioEnabled: true,
    };
  }, [roomUser]);

  const [connectionDetails, setConnectionDetails] = React.useState<ConnectionDetails | undefined>(
    undefined,
  );

  // Beam identity for this visit, lifted out of the URL into localStorage on
  // first load so the address bar is left as a shareable `?pageId=…`.
  // `null` = not resolved yet; resolution needs `window`, so it can't happen
  // during the server render that precedes it.
  const [session, setSession] = React.useState<SessionParams | null>(null);
  // Is this person on the page's member list? Not the same as "has a Beam
  // account" — someone who was handed the link may well be signed in to Beam
  // and still be a stranger to this meeting.
  const [isMember, setIsMember] = React.useState(false);
  // Does BDS actually know this page? Tracked separately from `isMember`
  // because "you are not on the member list" and "there is no member list"
  // have to lead to opposite decisions — see `needsApproval`.
  const [pageKnown, setPageKnown] = React.useState(false);

  // Waiting-room state. `lobbyTicket` is the claim check handed out by
  // /api/lobby/knock; `lobbyStatus` is what a moderator has done about it.
  const [lobbyTicket, setLobbyTicket] = React.useState<string | null>(null);
  const [lobbyStatus, setLobbyStatus] = React.useState<LobbyTicketStatus | null>(null);
  const [lobbyPosition, setLobbyPosition] = React.useState(0);
  const [joinError, setJoinError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSession(
      resolveSessionParams({
        userId: props.userId,
        partitionId: props.partitionId,
        metadata: props.randomParticipantPostfix || undefined,
      }),
    );
    // Mount only: after the first run the params are gone from the URL by
    // design, so re-running would just re-read what it already returned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Who has to be let in.
   *
   * Members of the page walk straight in, as they always have. Everybody else —
   * anyone who opened a shared link — types a name and joins a queue.
   *
   * Gated on `pageKnown`, and that is the load-bearing part. Without it, the two
   * cases where BDS returns no member list at all would both read as "you are
   * not a member":
   *
   *   - BDS is down or erroring. Everyone, hosts included, would be sent to a
   *     waiting room that by definition has nobody in it to open the door — one
   *     dependency failing would take every meeting with it.
   *   - The room is not a Beam page (the "Start Meeting" demo flow mints a
   *     random id). The person who just created it would queue at their own
   *     empty meeting.
   *
   * So an absent member list means "this is not a page with a guest list",
   * not "you are a guest". The cost is that a BDS outage lets link recipients
   * in unchecked; the alternative is that a BDS outage lets nobody in at all.
   *
   * `viewer=true` is also exempt: it is an explicit watch-only broadcast mode
   * whose PreJoin hides the name field entirely, so there is no name to queue
   * under and no media for a moderator to be gatekeeping.
   */
  const needsApproval = pageKnown && !isMember && props.viewer !== 'true';

  /**
   * Joins with mic, camera and screen share all off, and needs a moderator to
   * hand each one back.
   *
   * Two ways to land here, and they are the same situation from the room's point
   * of view — somebody is present who has not been vouched for as a contributor:
   *   - a member whose page permission is read-only
   *   - anyone admitted off a shared link, who has no page permission at all
   *
   * Being admitted through the waiting room deliberately does NOT carry a right
   * to speak: the moderator decided to let them watch, which is a smaller
   * decision than letting them talk, and conflating the two would mean a single
   * click hands the room to a stranger.
   *
   * Not applied to viewers — that mode already publishes nothing by design.
   */
  const isRestricted =
    props.viewer !== 'true' && (needsApproval || userRole === READ_ONLY_ROLE);

  /**
   * Mint a room token. The ONE place a token is asked for, whether the caller
   * walked in or was admitted — so the two paths can't drift apart.
   */
  const requestConnectionDetails = React.useCallback(
    async (username: string, ticket?: string): Promise<ConnectionDetails> => {
      const url = new URL(CONN_DETAILS_ENDPOINT, window.location.origin);
      url.searchParams.append('roomName', props.roomName);
      url.searchParams.append('participantName', username);
      if (ticket) {
        // An admitted guest's name and identity both come off their queue
        // entry server-side, so sending our own postfix here would only
        // give them two identities across a reconnect.
        url.searchParams.append('lobbyTicket', ticket);
      } else {
        url.searchParams.append('randomParticipantPostfix', session?.metadata || '');
        if (session?.userId) {
          // Tells the server this is a Beam user rather than a link recipient,
          // which is what lets them skip the queue.
          url.searchParams.append('userId', session.userId);
        }
      }
      if (props.region) {
        url.searchParams.append('region', props.region);
      }
      if (props.viewer) {
        url.searchParams.append('viewer', props.viewer);
      }
      // Seeds `role` into participant metadata so server-side code (the room-wide
      // mute sweep) can tell moderators apart — it has no other way to see this.
      if (userRole) {
        url.searchParams.append('userRole', userRole);
      }
      const resp = await fetch(url.toString());
      if (!resp.ok) {
        throw new Error((await resp.text()) || 'Could not join this meeting.');
      }
      return (await resp.json()) as ConnectionDetails;
    },
    // userRole must be in the deps — with the previous `[]` this closure would
    // capture the initial "" and never send the role.
    [props.roomName, props.region, props.viewer, session, userRole],
  );

  const handlePreJoinSubmit = React.useCallback(async (values: LocalUserChoices) => {
    const username = values.username.trim();
    setJoinError(null);
    setPreJoinChoices(values);

    if (!needsApproval) {
      try {
        setConnectionDetails(await requestConnectionDetails(username));
      } catch (e) {
        setPreJoinChoices(undefined);
        setJoinError(e instanceof Error ? e.message : 'Could not join this meeting.');
      }
      return;
    }

    // Knock and wait. Note this hands back a fresh ticket every time, so
    // reloading the waiting screen leaves the old request behind to expire on
    // its own — visible to a moderator as a duplicate for a few minutes.
    // Reusing a same-name request instead would let two different people called
    // "Alex" share one ticket, and admitting one would admit both.
    try {
      const resp = await fetch('/api/lobby/knock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName: props.roomName, name: username }),
      });
      if (!resp.ok) {
        throw new Error((await resp.text()) || 'Could not ask to join.');
      }
      const data: LobbyTicketResponse = await resp.json();
      setLobbyTicket(data.ticket);
      setLobbyPosition(data.position ?? 0);
      setLobbyStatus('pending');
    } catch (e) {
      setPreJoinChoices(undefined);
      setJoinError(e instanceof Error ? e.message : 'Could not ask to join.');
    }
  }, [needsApproval, requestConnectionDetails, props.roomName]);
  const handlePreJoinError = React.useCallback((e: any) => console.error(e), []);

  // Poll for a verdict. The only polling in the lobby: everyone already in the
  // room is pushed the queue via RoomMetadataChanged, but the person waiting is
  // by definition not in the room and has nothing to be pushed to.
  React.useEffect(() => {
    if (!lobbyTicket || lobbyStatus !== 'pending') return;
    let cancelled = false;

    const poll = async () => {
      try {
        const url = new URL('/api/lobby/status', window.location.origin);
        url.searchParams.set('roomName', props.roomName);
        url.searchParams.set('ticket', lobbyTicket);
        const resp = await fetch(url.toString());
        if (cancelled || !resp.ok) return;
        const data: LobbyTicketResponse = await resp.json();
        if (cancelled) return;
        setLobbyPosition(data.position ?? 0);
        if (data.status !== 'pending') setLobbyStatus(data.status);
      } catch {
        // Transient — the next tick will try again rather than dumping someone
        // out of the queue over one failed request.
      }
    };

    poll();
    const intervalId = setInterval(poll, LOBBY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [lobbyTicket, lobbyStatus, props.roomName]);

  // Admitted — redeem the ticket for a token and connect.
  React.useEffect(() => {
    if (lobbyStatus !== 'approved' || !lobbyTicket || !preJoinChoices) return;
    if (connectionDetails) return;
    let cancelled = false;

    (async () => {
      try {
        const details = await requestConnectionDetails(
          preJoinChoices.username.trim(),
          lobbyTicket,
        );
        if (!cancelled) setConnectionDetails(details);
      } catch (e) {
        if (cancelled) return;
        // The ticket aged out between the approval and the redemption. Send
        // them back to the start rather than leaving them on a screen that
        // says they're in.
        setLobbyTicket(null);
        setLobbyStatus(null);
        setPreJoinChoices(undefined);
        setJoinError(e instanceof Error ? e.message : 'Could not join this meeting.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    lobbyStatus,
    lobbyTicket,
    preJoinChoices,
    connectionDetails,
    requestConnectionDetails,
  ]);

  const resetJoinAttempt = React.useCallback(() => {
    // Withdraw a request we're abandoning, so it stops sitting in every
    // moderator's queue for the rest of its ten-minute TTL. Reuses the deny
    // path rather than adding a third status: to everyone else "they gave up"
    // and "they were turned away" mean the same thing — take them off the list
    // — and the person cancelling is the one who already knows the difference.
    // Fire-and-forget: they are going back to the form either way.
    if (lobbyTicket && lobbyStatus === 'pending') {
      fetch(
        `/api/lobby/decide?roomName=${encodeURIComponent(props.roomName)}&ticket=${encodeURIComponent(lobbyTicket)}&approve=false`,
      ).catch(() => {});
    }
    setLobbyTicket(null);
    setLobbyStatus(null);
    setLobbyPosition(0);
    setPreJoinChoices(undefined);
    setJoinError(null);
  }, [lobbyTicket, lobbyStatus, props.roomName]);

  // Viewers keep a real default. A participant with no name on their BDS profile
  // gets an empty field and has to type one — the old "Username" here was the
  // input's placeholder text being used as a value, which sailed through
  // PreJoin's built-in `username !== ''` check and joined people as "Username".
  const defaultUsername = props.viewer === 'true' ? "Guest" : ""

  // Fetch participants list for prejoin screen
  const fetchPreJoinParticipants = React.useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setIsLoadingParticipants(true);
      }
      const url = new URL('/api/participants/list', window.location.origin);
      url.searchParams.append('roomName', props.roomName);
      const response = await fetch(url.toString());
      
      if (response.ok) {
        const data = await response.json();
        setPreJoinParticipants(data.participants || []);
        setPreJoinPolicy(data.policy || {});
      } else {
        // If room doesn't exist or error, set empty array
        setPreJoinParticipants([]);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      setPreJoinParticipants([]);
    } finally {
      setIsLoadingParticipants(false);
    }
  }, [props.roomName]);

  // Format participant names for display
  const formatParticipantDisplay = React.useCallback((participants: string[]): string => {
    if (participants.length === 0) {
      return 'No one is in the call';
    } else if (participants.length === 1) {
      return `${participants[0]} is in the call`;
    } else if (participants.length === 2) {
      return `${participants[0]}, ${participants[1]} are in the call`;
    } else {
      const firstTwo = participants.slice(0, 2).join(', ');
      const remaining = participants.length - 2;
      return `${firstTwo} +${remaining} ${remaining === 1 ? 'user' : 'users'} are in the call`;
    }
  }, []);

  // Set up polling for participant updates when on prejoin screen
  React.useEffect(() => {
    // Only poll when connectionDetails is undefined (prejoin state)
    if (connectionDetails !== undefined) {
      return;
    }

    // Fetch immediately
    fetchPreJoinParticipants();

    // Set up polling interval (every 2.5 seconds)
    const intervalId = setInterval(() => {
      fetchPreJoinParticipants(true);
    }, 5000);

    // Cleanup interval on unmount or when user joins
    return () => {
      clearInterval(intervalId);
    };
  }, [connectionDetails, fetchPreJoinParticipants]);

  React.useEffect(() => {
    // Waits for the identity to be lifted out of the URL / read back out of
    // localStorage — every query below is keyed by it.
    if (!session) return;
    const userId = session.userId;
    const fetchData = async () => {
      localStorage.removeItem("lk-user-choices");
      const currentTime = moment().format('MM/DD/YYYY, HH:mm:ss A');
      try {
        const titleResponse = await fetch("https://bds-api.staging.beam.live/aggregateBds", {
          method: "POST",
          body: JSON.stringify({
            select: {
              index: {
                searchIndex: "beamdevlive:page:title",
                searchQuery: "*",
                searchOptions: "TIMEOUT 10000"
              },
              list: {
                objectIds: [props.roomName]
              },
              load: {
                title: "$..title",
                // No user id (a plain shared link) ⇒ nothing to look up, and a
                // literal `users.undefined` in the JSONPath would come back as
                // a match-shaped nothing. Ask only for the title.
                ...(userId ? { user: `$..members.users.${userId}` } : {})
              }
            },
            groupBy: {
              properties: [],
              reducers: []
            },
            apply: {
              expression: "",
              name: ""
            },
            result: {
              pagination: {
                offset: 0,
                limit: 1
              }
            },
            filter: {
              expression: ""
            },
            anyStatus: false
          }),
          headers: {
            "Content-Type": "application/json",
          },
        });
        
        console.log("Title Response: ", titleResponse.status)
        if (titleResponse.status == 201) {
          const titleData = await titleResponse.json();
          if (titleData["data"].length > 0) {
            setRoomTitle(titleData["data"][0]["title"])
            setPageKnown(true);
            // Optional-chained: a non-member has no `user` entry at all, and
            // indexing straight into it threw a TypeError that the catch below
            // swallowed — taking the room title down with it and leaving every
            // stranger looking at a timestamp instead of the meeting name.
            const member = titleData["data"][0]?.["user"];
            setIsMember(!!member);
            setUserRole(member?.["role"] ?? "");
          } else {
            setRoomTitle(currentTime)
            setPageKnown(false);
            setIsMember(false);
          }
        } else {
          setRoomTitle(currentTime)
          setPageKnown(false);
        }
      } catch (error) {
        console.error("API Request Failed in Room Title:", error);
        setRoomTitle(currentTime)
        // Leaves the waiting room switched off — see `needsApproval` for why a
        // failed lookup must not turn into "everybody is a guest".
        setPageKnown(false);
      }

      if (!userId) {
        // A shared link with no identity behind it. There is no profile to look
        // up, so leave the name field empty for them to fill in — and resolve
        // `roomUser` all the same, or the loading gate below never lifts.
        setRoomUser(defaultUsername);
        return;
      }

      try {
        const userResponse = await fetch("https://bds-api.staging.beam.live/aggregateBds", {
          method: "POST",
          body: JSON.stringify({
            "select": {
              "index": {
                "searchIndex": "beamdevlive:userNameIndex",
                "searchQuery": `@partitions:{'org\\:${session.partitionId?.replace("-", "\\-")}'}`,
                "searchOptions": "TIMEOUT 10000"
              },
              "list": {
                "objectIds": [
                  userId
                ]
              },
              "load": {
                "profile": "$.persistent.static.live"
              }
            },
            "groupBy": {
              "properties": [],
              "reducers": []
            },
            "apply": {
              "expression": "",
              "name": ""
            },
            "result": {
              "pagination": {
                "offset": 0,
                "limit": 1
              }
            },
            "filter": {
              "expression": ""
            },
            "anyStatus": false
        }),
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        console.log("Username Response: ", userResponse.status)
        if (userResponse.status == 201) {
          const userData = await userResponse.json();
          if (userData["data"].length > 0) {
            // A profile can exist with no name on it. Interpolating those
            // straight into a template string produced "undefined undefined"
            // (or a lone space) as the username, so build it from the parts
            // that are actually present and fall through when none are.
            const profile = userData["data"][0]?.["profile"]?.["profile"];
            const resolvedName = [profile?.["firstName"], profile?.["lastName"]]
              .filter((part: any) => typeof part === 'string' && part.trim().length > 0)
              .join(' ')
              .trim();
            setRoomUser(resolvedName || defaultUsername)
          } else {
            setRoomUser(defaultUsername)
          }
        } else {
          setRoomUser(defaultUsername)
        }        
      } catch (error) {
        console.error("API Request Failed in User Name:", error);
        setRoomUser(defaultUsername)
      }
    };
  
    fetchData();
  }, [props.roomName, session, defaultUsername]);

  // Strictly `undefined`: '' is a resolved state (no profile name) and must fall
  // through to PreJoin with an empty field, not sit here spinning.
  if (roomUser === undefined) {
    return (
      <main data-lk-theme="default" style={{ height: '100%' }}>
        <header>
          <img src="/images/logo.svg" alt="Beam Meet" />
        </header>
        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <div className="spinner"></div>
        </div>
      </main>
    );
  }

  // A verdict is pending, or has just landed and the token is being fetched.
  // Both read as "still waiting" to the person on this screen.
  const inLobby = lobbyStatus === 'pending' || lobbyStatus === 'approved';
  // 'expired' is a rejection only in the sense that they aren't getting in on
  // this ticket — the copy below distinguishes it from an actual refusal.
  const lobbyBlocked = lobbyStatus === 'denied' || lobbyStatus === 'expired';
  const inConference = connectionDetails !== undefined && preJoinChoices !== undefined;

  return (
    <main data-lk-theme="default" style={{ height: '100%' }}>
      {!inConference ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <header>
            <img src="/images/logo.svg" alt="Beam Meet" />
          </header>
          <div
            style={{ display: 'grid', placeItems: 'center', flex: 1 }}
            className={
              props.viewer === 'true'
                ? 'viewer-container'
                : isRestricted
                  ? 'joiner-container restricted-container'
                  : 'joiner-container'
            }
          >
            {inLobby ? (
              <LobbyWaitingCard
                roomTitle={roomTitle}
                name={preJoinChoices?.username?.trim() || roomUser}
                position={lobbyPosition}
                admitted={lobbyStatus === 'approved'}
                onCancel={resetJoinAttempt}
              />
            ) : lobbyBlocked ? (
              <LobbyBlockedCard
                expired={lobbyStatus === 'expired'}
                onRetry={resetJoinAttempt}
              />
            ) : (
            <div className="lk-prejoin-container">
            <p className="join-datetime">Join {roomTitle}</p>

            {needsApproval ? (
              <p className="lk-lobby-notice">
                A moderator has to let you in. Enter your name and we&apos;ll ask
                them for you. You&apos;ll join muted, with your camera off.
              </p>
            ) : isRestricted ? (
              <p className="lk-lobby-notice">
                You have view-only access to this page, so you&apos;ll join muted
                with your camera off. Raise your hand in the meeting to ask a
                moderator to let you speak.
              </p>
            ) : null}

            <PreJoin
              defaults={{
                ...preJoinDefaults,
                // Off for a restricted joiner too: their token forbids
                // publishing, so arming a camera here would only produce a
                // device permission prompt for a track the room then refuses.
                videoEnabled: props.viewer !== 'true' && !isRestricted,
                audioEnabled: props.viewer !== 'true' && !isRestricted,
              }}
              onSubmit={handlePreJoinSubmit}
              onError={handlePreJoinError}
              // PreJoin already disables Join on an empty username; this only
              // tightens its `!== ''` test so a field of spaces doesn't pass.
              onValidate={(values) => values.username.trim().length > 0}
              // PreJoin's loadUserChoices does {...defaults, ...localStorage}, so
              // a saved username WINS over `defaults` — including the literal
              // "Username" that earlier builds persisted. Opting out keeps the
              // BDS name (or an empty field) authoritative. Trade-off: device ids
              // stop persisting between visits; the enabled flags are set from
              // props above, so they're unaffected.
              persistUserChoices={false}
              joinLabel={
                props.viewer === 'true'
                  ? 'Watch Now'
                  : needsApproval
                    ? 'Ask to Join'
                    : 'Join Now'
              }
            >
            </PreJoin>

            {joinError && (
              <p className="lk-lobby-error" role="alert">
                {joinError}
              </p>
            )}

            {!isLoadingParticipants && (
              <p className="prejoin-participants" style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: 0,
                textAlign: 'center'
              }}>
                {formatParticipantDisplay(preJoinParticipants)}
              </p>
            )}

            </div>
            )}

          </div>
        </div>
      ) : (
        <VideoConferenceComponent
          connectionDetails={connectionDetails}
          userChoices={preJoinChoices}
          options={{ codec: props.codec, hq: props.hq }}
          viewer={props.viewer === 'true'}
          userRole={userRole}
          restricted={isRestricted}
          joinPolicy={preJoinPolicy}
        />
      )}
    </main>
  );
}

/**
 * What someone sees while a moderator decides about them.
 *
 * Deliberately not a spinner on its own: waiting with no idea whether anyone
 * has even seen the request is the worst version of this screen, so it names
 * the meeting, echoes back the name that was submitted, and says how many
 * people are ahead.
 */
function LobbyWaitingCard(props: {
  roomTitle: string;
  name: string;
  position: number;
  /** Approved — the token is on its way, which is a different kind of waiting. */
  admitted: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="lk-prejoin-container lk-lobby-card">
      <p className="join-datetime">{props.roomTitle}</p>
      <div className="spinner lk-lobby-spinner" />
      <h3 className="lk-lobby-heading">
        {props.admitted ? "You're in — connecting…" : 'Waiting to be let in'}
      </h3>
      {!props.admitted && (
        <>
          <p className="lk-lobby-body">
            {props.name ? <strong>{props.name}</strong> : 'You'} asked to join.
            A moderator will admit you shortly.
          </p>
          {props.position > 0 && (
            <p className="lk-lobby-body lk-lobby-position">
              {props.position} {props.position === 1 ? 'person is' : 'people are'} ahead of
              you.
            </p>
          )}
          <button type="button" className="lk-lobby-secondary-button" onClick={props.onCancel}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

/** The request was turned down, or sat unanswered long enough to expire. */
function LobbyBlockedCard(props: { expired: boolean; onRetry: () => void }) {
  return (
    <div className="lk-prejoin-container lk-lobby-card">
      <h3 className="lk-lobby-heading">
        {props.expired ? 'Your request expired' : "You weren't let in"}
      </h3>
      <p className="lk-lobby-body">
        {props.expired
          ? 'Nobody answered in time. You can ask again.'
          : 'A moderator declined your request to join this meeting.'}
      </p>
      <button type="button" className="lk-lobby-primary-button" onClick={props.onRetry}>
        Ask again
      </button>
    </div>
  );
}

function VideoConferenceComponent(props: {
  userChoices: LocalUserChoices;
  connectionDetails: ConnectionDetails;
  options: {
    hq: boolean;
    codec: VideoCodec;
  };
  viewer?: boolean;
  userRole?: string;
  /** Read-only or admitted-by-link; nothing publishes until a moderator says so. */
  restricted?: boolean;
  /** Room policy read during PreJoin; gates the initial publish. */
  joinPolicy?: RoomMediaPolicy;
}) {
  const e2eePassphrase =
    typeof window !== 'undefined' && decodePassphrase(location.hash.substring(1));

  const worker =
    typeof window !== 'undefined' &&
    e2eePassphrase &&
    new Worker(new URL('livekit-client/e2ee-worker', import.meta.url));
  const e2eeEnabled = !!(e2eePassphrase && worker);
  const keyProvider = new ExternalE2EEKeyProvider();
  const [e2eeSetupComplete, setE2eeSetupComplete] = React.useState(false);
  

  const roomOptions = React.useMemo((): RoomOptions => {
    let videoCodec: VideoCodec | undefined = props.options.codec ? props.options.codec : 'vp9';
    if (e2eeEnabled && (videoCodec === 'av1' || videoCodec === 'vp9')) {
      videoCodec = undefined;
    }
    return {
      videoCaptureDefaults: {
        deviceId: props.userChoices.videoDeviceId ?? undefined,
        resolution: props.options.hq ? VideoPresets.h2160 : VideoPresets.h720,
      },
      publishDefaults: {
        dtx: false,
        videoSimulcastLayers: props.options.hq
          ? [VideoPresets.h1080, VideoPresets.h720]
          : [VideoPresets.h540, VideoPresets.h216],
        red: !e2eeEnabled,
        videoCodec,
      },
      audioCaptureDefaults: {
        deviceId: props.userChoices.audioDeviceId ?? undefined,
      },
      adaptiveStream: { pixelDensity: 'screen' },
      dynacast: true,
      e2ee: e2eeEnabled
        ? {
            keyProvider,
            worker,
          }
        : undefined,
    };
  }, [props.userChoices, props.options.hq, props.options.codec]);

  const room = React.useMemo(() => new Room(roomOptions), []);

  // React.useEffect(() => {
  //   if (room && !props.viewer) {
  //     // const wsUrl = process.env.NEXT_PUBLIC_WS_STREAM_URL || "";
  //     // const streamer = new WebSocketStreamer(room, wsUrl);

  //     fetch(`/api/stream/start?roomName=${encodeURIComponent(props.connectionDetails.roomName)}`, {
  //       method: 'GET',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //     }).catch(error => {
  //       console.error('Failed to start stream:', error);
  //     });

  //     // return () => {
  //     //   streamer.close();
  //     // };
  //   }
  // }, [room, props.viewer]);

  React.useEffect(() => {
    if (e2eeEnabled) {
      keyProvider
        .setKey(decodePassphrase(e2eePassphrase))
        .then(() => {
          room.setE2EEEnabled(true).catch((e) => {
            if (e instanceof DeviceUnsupportedError) {
              alert(
                `You're trying to join an encrypted meeting, but your browser does not support it. Please update it to the latest version and try again.`,
              );
              console.error(e);
            } else {
              throw e;
            }
          });
        })
        .then(() => setE2eeSetupComplete(true));
    } else {
      setE2eeSetupComplete(true);
    }
  }, [e2eeEnabled, room, e2eePassphrase]);

  const connectOptions = React.useMemo((): RoomConnectOptions => {
    return {
      autoSubscribe: true,
    };
  }, []);

  const router = useRouter();
  const handleOnLeave = React.useCallback(() => {
    window.location.reload();
  }, [router]);
  const handleError = React.useCallback((error: Error) => {
    console.error(error);
    // alert(`Encountered an unexpected error, check the console logs for details: ${error.message}`);
  }, []);
  const handleEncryptionError = React.useCallback((error: Error) => {
    console.error(error);
    alert(
      `Encountered an unexpected encryption error, check the console logs for details: ${error.message}`,
    );
  }, []);

  return (
    <>
      <LiveKitRoom
        connect={e2eeSetupComplete}
        room={room}
        token={props.connectionDetails.participantToken}
        serverUrl={props.connectionDetails.serverUrl}
        connectOptions={connectOptions}
        // A restricted participant's token has canPublish: false, so letting
        // LiveKitRoom try to publish on connect would just throw.
        video={props.userChoices.videoEnabled && !props.joinPolicy?.cameraOffAll && !props.restricted}
        audio={props.userChoices.audioEnabled && !props.joinPolicy?.muteAll && !props.restricted}
        onDisconnected={handleOnLeave}
        onEncryptionError={handleEncryptionError}
        onError={handleError}      
      >
        <CustomVideoConference
          chatMessageFormatter={formatChatMessageLinks}
          SettingsComponent={SHOW_SETTINGS_MENU ? SettingsMenu : undefined}
          viewer={props.viewer}
          userRole={props.userRole}
          restricted={props.restricted}
        />
        <DebugMode />
        <RecordingIndicator />
      </LiveKitRoom>
    </>
  );
}
